import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, OnDestroy, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { getDownloadURL, ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AccountingService } from '../../../../services/accounting.service';
import { AuthService } from '../../../../services/auth.service';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as groupActions from '../../../../state/actions/group.actions';
import { DialogService } from '../../../components/dialogs/dialog.service';
import { AlertType } from '../../../components/dialogs/confirm.dialog.model';
import { UtilsService } from '../../../../services/utils.service';
import { AppExceptionModel } from '../../../../models/app-exception.model';

@Component({
  selector: 'app-create-expense-report',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './create-expense-report.component.html',
})
export class CreateExpenseReportComponent implements OnInit, OnDestroy {
  private accountingService: AccountingService = inject(AccountingService);
  private auth: AuthService = inject(AuthService);
  private toastr: ToastrService = inject(ToastrService);
  private storage: Storage = inject(Storage);
  private store: Store<AppState> = inject(Store<AppState>);
  private router: Router = inject(Router);
  private dialogService: DialogService = inject(DialogService);
  private utilsService: UtilsService = inject(UtilsService);
  private actions$: Actions = inject(Actions);
  private logger = inject(NGXLogger);
  private destroy$ = new Subject<void>();

  @Input() groupId: string | null = null;
  @Input() report: any = null; // If passed, we are editing this draft
  @Input() isTreasurer = false;
  @Input() groupCurrency = 'USD';

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<void>();

  title = '';
  expenses: any[] = [];
  userId: string | null = null;
  reportId = '';
  uploadedReceipts: { [expenseId: string]: string } = {};

  showExpenseModal = false;
  expenseForm!: FormGroup;
  selectedFile: File | null = null;
  isUploadingFile = false;
  isScanningReceipt = false;
  isSavingReport = false;
  editingExpenseIndex: number | null = null;

  ngOnInit(): void {
    this.userId = this.auth.getUid() || null;
    this.reportId = this.report?.id || this.accountingService.createId();
    this.initExpenseForm();

    this.store.select(groupSelector.getExtractedReceipt)
      .pipe(takeUntil(this.destroy$))
      .subscribe((extraction) => {
        if (extraction) {
          // If there is an error
          if (extraction.error) {
            // Only handle if it's for this receipt
            if (!extraction.receiptId || extraction.receiptId === this.expenseForm.get('id')?.value) {
              this.logger.error('Receipt scan failure:', extraction.errorMessage);
              const errMsg = extraction.errorMessage || 'AI was unable to extract details from this receipt.';
              this.utilsService.onShowAlert({
                title: 'Receipt Scan Failed',
                message: errMsg,
              } as AppExceptionModel, false);
              this.store.dispatch(groupActions.clearExtractedReceipt());
            }
            return;
          }

          // If the extraction is for a different receipt (e.g. from a batch upload), ignore it
          if (extraction.receiptId && extraction.receiptId !== this.expenseForm.get('id')?.value) {
            return;
          }

          this.expenseForm.patchValue({
            amount: extraction.amount || '',
            currency: extraction.currency?.toLowerCase() || this.groupCurrency.toLowerCase() || 'usd',
            date: (extraction.date && typeof extraction.date === 'string')
              ? extraction.date.substring(0, 10)
              : new Date().toISOString().substring(0, 10),
            description: extraction.description || '',
            from: extraction.from || '',
            to: extraction.to || '',
            tags: (extraction.tags && Array.isArray(extraction.tags))
              ? extraction.tags.join(', ')
              : (typeof extraction.tags === 'string' ? extraction.tags : ''),
          });
          this.toastr.success('AI successfully extracted receipt details!', 'Extraction Success');
          this.store.dispatch(groupActions.clearExtractedReceipt());
        }
      });

    this.store.select(groupSelector.getExtractingReceipt)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isScanning) => {
        this.isScanningReceipt = isScanning;
        if (this.expenseForm) {
          if (isScanning) {
            this.expenseForm.disable();
          } else {
            this.expenseForm.enable();
          }
        }
      });

    if (this.report) {
      this.title = this.report.title || '';
      this.expenses = this.report.expenses ? [...this.report.expenses] : [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initExpenseForm(data?: any): void {
    const fileId = data?.id || Math.random().toString(36).substring(2);
    this.expenseForm = new FormGroup({
      id: new FormControl(fileId),
      amount: new FormControl(data?.amount || '', [
        Validators.required,
        Validators.min(0.01),
      ]),
      currency: new FormControl((data?.currency || this.groupCurrency || 'usd').toLowerCase(), [
        Validators.required,
      ]),
      conversionRate: new FormControl(data?.conversionRate || 1, [
        Validators.required,
        Validators.min(0.000001),
      ]),
      date: new FormControl(data?.date || new Date().toISOString().substring(0, 10), [
        Validators.required,
      ]),
      description: new FormControl(data?.description || '', [
        Validators.required,
        Validators.minLength(3),
      ]),
      from: new FormControl(data?.from || ''),
      to: new FormControl(data?.to || '', [Validators.required]),
      tags: new FormControl(data?.tags || ''),
    });
  }

  get totalAmount(): number {
    return this.expenses.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      const rate = parseFloat(item.conversionRate) || 1;
      return sum + (amount * rate);
    }, 0);
  }

  openAddExpenseModal(): void {
    this.editingExpenseIndex = null;
    this.selectedFile = null;
    const newId = Math.random().toString(36).substring(2);
    this.initExpenseForm({ id: newId });
    this.showExpenseModal = true;
  }

  openEditExpenseModal(index: number): void {
    this.editingExpenseIndex = index;
    this.selectedFile = null;
    const exp = { ...this.expenses[index] };
    if (!exp.id) {
      exp.id = Math.random().toString(36).substring(2);
    }
    this.initExpenseForm(exp);
    this.showExpenseModal = true;
  }

  deleteExpense(index: number): void {
    this.expenses.splice(index, 1);
    this.toastr.info('Expense removed from report');
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      this.toastr.error('Receipt file size exceeds 5MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Invalid file type. Only JPG, JPEG, PNG, and PDF are allowed.');
      return;
    }

    this.selectedFile = file;
  }

  async scanReceipt(): Promise<void> {
    if (!this.selectedFile) {
      this.toastr.warning('Please select a receipt file to scan first.');
      return;
    }

    const isAvailable = await firstValueFrom(this.store.select(groupSelector.isAiCreditsAvailable));
    if (!isAvailable) {
      const buyNow = await this.dialogService.confirm({
        title: 'Out of AI Credits',
        description: 'Your group has run out of AI credits. Please subscribe or buy more credits to continue using the AI receipt scanner.',
        action: 'Buy Now',
        cancelText: 'Buy Later',
      });
      if (buyNow && this.groupId) {
        this.router.navigate(['/school', this.groupId, 'billing', 'addonsubscribe']);
      }
      return;
    }

    this.isScanningReceipt = true;

    try {
      const expenseId = this.expenseForm.get('id')?.value;
      if (!expenseId) {
        throw new Error('Expense ID is missing.');
      }

      let receiptUrl = this.uploadedReceipts[expenseId];
      if (!receiptUrl) {
        receiptUrl = await this.uploadReceiptFileForId(this.selectedFile, expenseId);
        this.uploadedReceipts[expenseId] = receiptUrl;
      }

      const extension = this.selectedFile.name.split('.').pop() || 'pdf';
      const fileName = `${expenseId}.${extension}`;
      const storageRef = ref(this.storage, `groups/${this.groupId}/receipts/${this.reportId}/${fileName}`);
      const gcsPath = `gs://${storageRef.bucket}/${storageRef.fullPath}`;

      if (this.groupId) {
        this.store.dispatch(groupActions.extractReceipt({
          groupId: this.groupId,
          gcsPath: gcsPath,
          mimeType: this.selectedFile.type,
          receiptId: expenseId,
        }));
      } else {
        this.toastr.error('Group ID is missing. Cannot scan.');
        this.isScanningReceipt = false;
        if (this.expenseForm) {
          this.expenseForm.enable();
        }
      }
    } catch (error: any) {
      console.error(error);
      this.toastr.error('AI failed to parse the receipt. You can still input details manually.');
      this.isScanningReceipt = false;
    }
  }

  async addExpenseToReport(): Promise<void> {
    if (this.expenseForm.invalid || !this.groupId) {
      this.toastr.error('Please fill in all required fields.');
      return;
    }

    this.isUploadingFile = true;
    const expenseId = this.expenseForm.get('id')?.value;
    let receiptUrl = this.uploadedReceipts[expenseId] || 
                     (this.editingExpenseIndex !== null ? this.expenses[this.editingExpenseIndex].receiptUrl : '');

    try {
      if (this.selectedFile && !this.uploadedReceipts[expenseId]) {
        receiptUrl = await this.uploadReceiptFileForId(this.selectedFile, expenseId);
        this.uploadedReceipts[expenseId] = receiptUrl;
      }

      const expense = {
        id: expenseId,
        amount: parseFloat(this.expenseForm.value.amount),
        currency: this.expenseForm.value.currency,
        conversionRate: this.expenseForm.value.currency !== (this.groupCurrency || 'USD').toLowerCase()
          ? parseFloat(this.expenseForm.value.conversionRate || 1)
          : 1,
        date: this.expenseForm.value.date,
        description: this.expenseForm.value.description,
        from: this.expenseForm.value.from || '',
        to: this.expenseForm.value.to,
        tags: this.expenseForm.value.tags || '',
        receiptUrl,
        recordedBy: this.userId,
      };

      if (this.editingExpenseIndex !== null) {
        this.expenses[this.editingExpenseIndex] = expense;
        this.toastr.success('Expense updated');
      } else {
        this.expenses.push(expense);
        this.toastr.success('Expense added to report');
      }

      this.showExpenseModal = false;
    } catch (err: any) {
      console.error(err);
      this.toastr.error('Failed to save receipt attachment.');
    } finally {
      this.isUploadingFile = false;
    }
  }

  async saveDraft(): Promise<void> {
    if (!this.title.trim()) {
      this.toastr.error('Report Title is required.');
      return;
    }

    if (!this.groupId) return;
    this.isSavingReport = true;

    try {
      const reportPayload = {
        ...this.report,
        id: this.reportId,
        title: this.title.trim(),
        status: 'draft',
        totalAmount: this.totalAmount,
        currency: this.groupCurrency,
        expenses: this.expenses,
        createdById: this.userId,
        createdBy: this.report?.createdBy || this.auth.getUserName() || 'Unknown',
        created: this.report?.created ? new Date(this.report.created) : new Date(),
      };

      await this.accountingService.saveExpenseReport(this.groupId, reportPayload);
      this.toastr.success('Expense report draft saved successfully.');
      this.onSave.emit();
    } catch (err: any) {
      console.error(err);
      this.toastr.error('Failed to save report draft.');
    } finally {
      this.isSavingReport = false;
    }
  }

  async publishReport(): Promise<void> {
    if (!this.title.trim()) {
      this.toastr.error('Report Title is required.');
      return;
    }

    if (this.expenses.length === 0) {
      this.toastr.error('Please add at least one expense before publishing.');
      return;
    }

    if (!this.groupId) return;
    this.isSavingReport = true;

    try {
      const reportPayload = {
        ...this.report,
        id: this.reportId,
        title: this.title.trim(),
        status: 'draft',
        totalAmount: this.totalAmount,
        currency: this.groupCurrency,
        expenses: this.expenses,
        createdById: this.userId,
        createdBy: this.report?.createdBy || this.auth.getUserName() || 'Unknown',
        created: this.report?.created ? new Date(this.report.created) : new Date(),
      };

      await this.accountingService.saveExpenseReport(this.groupId, reportPayload);
      await this.accountingService.publishExpenseReport(this.groupId, this.reportId, this.expenses);
      this.toastr.success('Expense report published to ledger successfully.');
      this.onSave.emit();
    } catch (err: any) {
      console.error(err);
      this.toastr.error('Failed to publish expense report.');
    } finally {
      this.isSavingReport = false;
    }
  }

  private uploadReceiptFileForId(file: File, expenseId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const extension = file.name.split('.').pop() || 'pdf';
      const fileName = `${expenseId}.${extension}`;
      const storageRef = ref(this.storage, `groups/${this.groupId}/receipts/${this.reportId}/${fileName}`);
      const metadata = { contentType: file.type };
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      uploadTask.on(
        'state_changed',
        null,
        (error) => reject(error),
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(
            (downloadURL) => resolve(downloadURL),
            (err) => reject(err)
          );
        }
      );
    });
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }
}
